import React, { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../../context/AppContext';
import supabase from '../../supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import SocialIntegrations from './SocialIntegrations';
import MigrationTool from './MigrationTool';
import NewsletterDashboard from './NewsletterDashboard';

const { 
  FiBriefcase, FiMail, FiCheck, FiX, FiClock, 
  FiAlertCircle, FiRefreshCw, FiEye, 
  FiTrash2, FiZap, FiShare2, FiMapPin, FiTag, FiChevronDown, FiChevronUp,
  FiSearch, FiGlobe, FiExternalLink, FiActivity, FiSend, FiUsers, FiFileText,
  FiArrowRight, FiCalendar
} = FiIcons;

const JobAdminCard = ({ job, onApprove, onDelete, onRenew, isPending, isExpired }) => {
  const [expanded, setExpanded] = useState(false);
  const [processing, setProcessing] = useState(false);

  const handleApprove = async () => {
    setProcessing(true);
    await onApprove(job.id);
    setProcessing(false);
  };

  const handleDelete = async () => {
    setProcessing(true);
    await onDelete(job.id);
    setProcessing(false);
  };

  const handleRenew = async () => {
    setProcessing(true);
    await onRenew(job.id);
    setProcessing(false);
  };

  return (
    <div className={`bg-white rounded-[32px] border ${expanded ? 'border-emerald-200 shadow-md' : 'border-stone-200 shadow-sm'} p-6 md:p-8 flex flex-col gap-6 group transition-all relative ${processing ? 'opacity-50 pointer-events-none' : ''}`}>
      <div className="flex flex-col md:flex-row gap-8 items-center">
        <div className="w-20 h-20 bg-stone-100 rounded-3xl flex-shrink-0 flex items-center justify-center overflow-hidden border border-stone-200">
          {job.profile?.logo_url ? <img src={job.profile.logo_url} className="w-full h-full object-cover" /> : <SafeIcon icon={FiBriefcase} className="text-2xl text-stone-300" />}
        </div>
        <div className="flex-1 text-center md:text-left">
          <div className="flex items-center gap-3 justify-center md:justify-start mb-2">
            <h3 className="text-xl font-black text-stone-900 uppercase tracking-tight">{job.title}</h3>
            {job.is_featured && <span className="bg-amber-100 text-amber-700 text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest flex items-center gap-1"><SafeIcon icon={FiZap} /> Featured</span>}
            {isExpired && <span className="bg-red-100 text-red-700 text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest flex items-center gap-1">Expired</span>}
          </div>
          <p className="text-stone-500 font-bold uppercase tracking-widest text-[10px] flex flex-wrap items-center justify-center md:justify-start gap-4">
            <span>{job.profile?.org_name}</span>
            <span className="w-1 h-1 bg-stone-300 rounded-full" />
            <span>{job.job_type}</span>
            <span className="w-1 h-1 bg-stone-300 rounded-full" />
            <span>{job.category}</span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setExpanded(!expanded)} className="p-4 text-stone-400 hover:text-stone-900 hover:bg-stone-50 rounded-2xl transition-all flex items-center gap-2 text-[10px] font-black uppercase tracking-widest">
            <SafeIcon icon={expanded ? FiChevronUp : FiEye} className="text-lg" />
            {expanded ? 'Hide Details' : 'View Details'}
          </button>
          {isPending && (
            <button onClick={handleApprove} className="px-8 py-4 bg-emerald-600 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-emerald-700 shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-2">
              <SafeIcon icon={FiCheck} className="text-lg" /> Approve
            </button>
          )}
          {isExpired && (
            <button onClick={handleRenew} className="px-8 py-4 bg-emerald-600 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-emerald-700 shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-2">
              <SafeIcon icon={FiCalendar} className="text-lg" /> Renew 30 Days
            </button>
          )}
          <button onClick={handleDelete} className="p-4 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-2xl transition-all">
            <SafeIcon icon={FiTrash2} className="text-lg" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="pt-6 border-t border-stone-100 grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-4">Job Description</h4>
                <div className="text-stone-600 text-sm leading-relaxed whitespace-pre-wrap bg-stone-50 p-6 rounded-2xl border border-stone-200">
                  {job.description || "No description provided."}
                </div>
              </div>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-4">Locations</h4>
                    <div className="flex flex-wrap gap-2">
                      {job.locations?.map((loc, i) => (
                        <span key={i} className="flex items-center gap-2 bg-white border border-stone-200 px-3 py-1.5 rounded-full text-[10px] font-bold text-stone-600">
                          <SafeIcon icon={FiMapPin} className="text-emerald-500" /> {loc.name}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-4">Expiration</h4>
                    <div className="flex items-center gap-2 bg-stone-100 px-3 py-1.5 rounded-full text-[10px] font-bold text-stone-600">
                      <SafeIcon icon={FiCalendar} className="text-emerald-500" /> 
                      {job.expires_at ? new Date(job.expires_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : 'No Expiry'}
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-4">Tags & Perks</h4>
                  <div className="flex flex-wrap gap-2">
                    {job.tags?.map((tag, i) => (
                      <span key={i} className="flex items-center gap-2 bg-stone-100 px-3 py-1.5 rounded-full text-[10px] font-bold text-stone-600">
                        <SafeIcon icon={FiTag} className="text-stone-400" /> {tag.tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-2">Application Instructions</h4>
                  <p className="text-xs font-bold text-stone-900 mb-2 uppercase tracking-widest">Method: {job.application_method || 'Link'}</p>
                  <div className="bg-stone-50 p-4 rounded-xl border border-stone-200">
                    {job.application_method === 'email' ? (
                      <span className="text-emerald-600 font-bold text-xs uppercase tracking-widest">{job.application_instructions}</span>
                    ) : job.application_method === 'link' ? (
                      <a href={job.application_instructions || job.application_link} target="_blank" rel="noopener noreferrer" className="text-emerald-600 text-xs font-bold hover:underline break-all uppercase tracking-widest">
                        {job.application_instructions || job.application_link}
                      </a>
                    ) : (
                      <p className="text-stone-600 text-xs leading-relaxed whitespace-pre-wrap">{job.application_instructions}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const AdminDashboard = () => {
  const { jobs, deleteJob, updateJobStatus, renewJob, isAdmin, fetchJobs } = useAppContext();
  const [activeTab, setActiveTab] = useState('jobs');
  const [emailQueue, setEmailQueue] = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [retrying, setRetrying] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);

  const fetchEmailQueue = useCallback(async () => {
    setQueueLoading(true);
    try {
      const { data, error } = await supabase.from('email_queue_20240320').select('*').order('created_at', { ascending: false }).limit(50);
      if (error) throw error;
      setEmailQueue(data || []);
    } catch (err) { console.error(err); } finally { setQueueLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'queue') fetchEmailQueue();
    if (activeTab === 'jobs') fetchJobs();
  }, [activeTab, fetchEmailQueue, fetchJobs]);

  const handleApprove = async (jobId) => {
    if (!confirm('Approve and publish this job?')) return;
    const res = await updateJobStatus(jobId, 'active');
    if (!res.success) alert('Error approving job: ' + res.error);
  };

  const handleDelete = async (jobId) => {
    if (!confirm('Are you sure you want to permanently delete this job? This action cannot be undone.')) return;
    const res = await deleteJob(jobId);
    if (!res.success) alert('Error deleting job: ' + res.error);
  };

  const handleRenew = async (jobId) => {
    if (!confirm('Extend this listing for another 30 days?')) return;
    const res = await renewJob(jobId);
    if (!res.success) alert('Error renewing job: ' + res.error);
  };

  const handleRetryEmail = async (emailId) => {
    setRetrying(emailId);
    try {
      const { error } = await supabase.functions.invoke('socketlabs_send_email', { body: { queueId: emailId } });
      if (error) throw error;
      fetchEmailQueue();
    } catch (err) { alert('Retry failed: ' + err.message); } finally { setRetrying(null); }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <SafeIcon icon={FiAlertCircle} className="text-4xl text-stone-300 mx-auto mb-4" />
          <h2 className="text-xl font-black text-stone-900 uppercase tracking-widest">Restricted Access</h2>
        </div>
      </div>
    );
  }

  const now = new Date();
  const pendingJobs = jobs.filter(j => j.status === 'pending');
  const expiredJobs = jobs.filter(j => {
    const isActuallyExpired = j.expires_at && new Date(j.expires_at) < now;
    return j.status === 'expired' || ((j.status === 'active' || j.status === 'published') && isActuallyExpired);
  });
  const activeJobs = jobs.filter(j => {
    const isActuallyExpired = j.expires_at && new Date(j.expires_at) < now;
    return (j.status === 'active' || j.status === 'published') && !isActuallyExpired;
  });

  const tabs = [
    { id: 'jobs', label: 'Jobs', icon: FiBriefcase, count: pendingJobs.length },
    { id: 'migration', label: 'Migration', icon: FiArrowRight },
    { id: 'newsletter', label: 'Newsletter', icon: FiMail },
    { id: 'queue', label: 'Queue', icon: FiActivity },
    { id: 'social', label: 'Social', icon: FiShare2 },
    { id: 'seo', label: 'SEO', icon: FiSearch },
  ];

  return (
    <div className="min-h-screen bg-stone-50 p-4 md:p-12">
      <div className="max-w-7xl mx-auto">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <h1 className="text-5xl font-black text-stone-900 tracking-tighter uppercase mb-2">Command Center</h1>
            <p className="text-stone-500 font-bold uppercase tracking-widest text-xs">System Administration & Oversight</p>
          </div>
          <div className="flex flex-wrap gap-2 bg-stone-200/50 p-1.5 rounded-[20px] backdrop-blur-sm self-start">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-[14px] text-[10px] font-black uppercase tracking-widest transition-all ${
                  activeTab === tab.id
                    ? tab.id === 'migration' ? 'bg-violet-600 text-white shadow-sm' : 'bg-white text-stone-900 shadow-sm'
                    : 'text-stone-500 hover:text-stone-700'
                }`}>
                <SafeIcon icon={tab.icon} />
                {tab.label}
                {tab.count !== undefined && <span className="ml-1 opacity-40">{tab.count}</span>}
              </button>
            ))}
          </div>
        </header>

        <main>
          {activeTab === 'jobs' && (
            <div className="space-y-16">
              <section>
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center text-amber-600"><SafeIcon icon={FiClock} /></div>
                  <div>
                    <h2 className="text-2xl font-black text-stone-900 uppercase tracking-tight">Pending Approval</h2>
                    <p className="text-stone-500 font-bold uppercase tracking-widest text-[8px]">New jobs waiting for review</p>
                  </div>
                </div>
                <div className="grid gap-6">
                  {pendingJobs.length > 0 ? pendingJobs.map(job => (
                    <JobAdminCard key={job.id} job={job} onApprove={handleApprove} onDelete={handleDelete} onRenew={handleRenew} isPending={true} />
                  )) : (
                    <div className="py-24 text-center bg-white rounded-[40px] border border-stone-200 border-dashed text-stone-400 font-black uppercase text-xs">Queue Clear. No pending approvals.</div>
                  )}
                </div>
              </section>

              {expiredJobs.length > 0 && (
                <section>
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center text-red-600"><SafeIcon icon={FiAlertCircle} /></div>
                    <div>
                      <h2 className="text-2xl font-black text-stone-900 uppercase tracking-tight">Expired Expeditions</h2>
                      <p className="text-stone-500 font-bold uppercase tracking-widest text-[8px]">Listings that have run their course</p>
                    </div>
                  </div>
                  <div className="grid gap-6">
                    {expiredJobs.map(job => (
                      <JobAdminCard key={job.id} job={job} onApprove={handleApprove} onDelete={handleDelete} onRenew={handleRenew} isPending={false} isExpired={true} />
                    ))}
                  </div>
                </section>
              )}

              <section>
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600"><SafeIcon icon={FiCheck} /></div>
                  <div>
                    <h2 className="text-2xl font-black text-stone-900 uppercase tracking-tight">Active Jobs</h2>
                    <p className="text-stone-500 font-bold uppercase tracking-widest text-[8px]">Live jobs in the field</p>
                  </div>
                </div>
                <div className="grid gap-6">
                  {activeJobs.length > 0 ? activeJobs.map(job => (
                    <JobAdminCard key={job.id} job={job} onApprove={handleApprove} onDelete={handleDelete} onRenew={handleRenew} isPending={false} />
                  )) : (
                    <div className="py-12 text-center bg-stone-100/50 rounded-[40px] border border-stone-200 border-dashed text-stone-400 font-bold uppercase tracking-widest text-[10px]">No active jobs found.</div>
                  )}
                </div>
              </section>
            </div>
          )}

          {activeTab === 'migration' && <MigrationTool />}
          {activeTab === 'newsletter' && <NewsletterDashboard />}

          {activeTab === 'queue' && (
            <div className="bg-white rounded-[40px] border border-stone-200 overflow-hidden shadow-sm">
              <div className="p-8 border-b border-stone-100 flex justify-between items-center bg-stone-50/50">
                <h3 className="font-black text-stone-900 uppercase tracking-widest text-xs">Live Email Queue</h3>
                <button onClick={fetchEmailQueue} disabled={queueLoading} className="p-2 hover:bg-stone-200 rounded-full transition-all"><SafeIcon icon={FiRefreshCw} className={queueLoading ? 'animate-spin' : ''} /></button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-stone-50/30 border-b border-stone-100">
                    <tr className="text-[10px] font-black text-stone-400 uppercase tracking-[0.2em]">
                      <th className="px-8 py-4">Recipient</th>
                      <th className="px-8 py-4">Subject</th>
                      <th className="px-8 py-4">Status</th>
                      <th className="px-8 py-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {emailQueue.map(item => (
                      <tr key={item.id} className="text-xs font-bold text-stone-700 group hover:bg-stone-50/50 transition-all">
                        <td className="px-8 py-4">{item.to_email}</td>
                        <td className="px-8 py-4 truncate max-w-[200px]">{item.subject}</td>
                        <td className="px-8 py-4">
                          <span className={`px-2 py-1 rounded-full text-[8px] uppercase tracking-widest ${item.status === 'success' ? 'bg-emerald-100 text-emerald-700' : item.status === 'error' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                            {item.status}
                          </span>
                        </td>
                        <td className="px-8 py-4 text-right">
                          {(item.status === 'error' || item.status === 'pending') && (
                            <button onClick={() => handleRetryEmail(item.id)} disabled={retrying === item.id}
                              className="px-4 py-2 bg-stone-900 text-white rounded-lg text-[10px] uppercase font-black hover:bg-stone-800 disabled:opacity-50">
                              {retrying === item.id ? <FiRefreshCw className="animate-spin" /> : 'Retry'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'social' && <SocialIntegrations />}
          {activeTab === 'seo' && <SEODashboard />}
        </main>
      </div>

      <AnimatePresence>
        {errorDetails && (
          <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 1 }} className="bg-white rounded-[32px] p-8 max-w-2xl w-full shadow-2xl border border-stone-200">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-black text-stone-900 uppercase tracking-widest text-sm flex items-center gap-2 text-red-600"><SafeIcon icon={FiAlertCircle} /> Diagnostic Report</h3>
                <button onClick={() => setErrorDetails(null)} className="p-2 hover:bg-stone-100 rounded-full transition-all"><SafeIcon icon={FiX} /></button>
              </div>
              <div className="bg-stone-50 rounded-2xl p-6 font-mono text-[11px] text-stone-600 overflow-auto max-h-[400px] border border-stone-200">
                <pre>{JSON.stringify(emailQueue.find(e => e.id === errorDetails)?.error_detail, null, 2)}</pre>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDashboard;